import { motion, useReducedMotion } from "framer-motion";
import { useTheme } from "../ThemeContext";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { DemoRequestDialog } from "@/components/DemoRequestDialog";
import { Activity, Calendar } from "lucide-react";
import ShinyText from "../reactbits/ShinyText";

const steps = [
    {
        title: "Get in Touch",
        description: "Schedule a demo to connect with our clinical implementation team",
        color: "#3b82f6",
    },
    {
        title: "Onboard Your Team",
        description: "Invite clinic administrators, therapists, and support staff to your secure Quilt workspace.",
        color: "#a855f7",
    },
    {
        title: "Enroll Your Patients",
        description: "Import your patient list via Quilt’s secure API",
        color: "#17a1a8",
    },
    {
        title: "Go Live",
        description: "You’re all set! Start reviewing and publishing content.",
        color: "#f59e0b",
    },
];

/** Three chevrons that light up in turn, blending from one step's colour to the next: the "this leads to that" cue. */
function FlowChevrons({ from, to, reduce }: { from: string; to: string; reduce: boolean | null }) {
    return (
        <span aria-hidden="true" className="absolute left-1/2 top-[calc(100%+4px)] z-10 flex h-8 -translate-x-1/2 rotate-90 items-center lg:left-[calc(100%+8px)] lg:top-1/2 lg:h-auto lg:-translate-y-1/2 lg:translate-x-0 lg:rotate-0">
            {[0, 1, 2].map((i) => (
                <motion.svg
                    key={i}
                    viewBox="0 0 10 16"
                    className="-mx-[3px] h-5 w-3"
                    fill="none"
                    stroke={`color-mix(in srgb, ${to} ${i * 50}%, ${from})`}
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ opacity: reduce ? 0.9 : 0.2 }}
                    animate={reduce ? undefined : { opacity: [0.2, 1, 0.2], x: [0, 2, 0] }}
                    transition={{ duration: 1.5, delay: i * 0.18, repeat: Infinity, ease: "easeInOut" }}
                >
                    <path d="M2 2l6 6-6 6" />
                </motion.svg>
            ))}
        </span>
    );
}

export function ClinicOnboarding() {
    const { theme } = useTheme();
    const reduce = useReducedMotion();

    return (
        <div className="w-full max-w-6xl mx-auto py-10 md:py-24 px-4">
            <div className="text-center mb-7 md:mb-16">
                <Badge variant="outline" className="mb-3 md:mb-4 px-4 py-2 bg-blue-500/10 border-blue-500/30">
                    <Activity className="w-4 h-4 mr-2 text-blue-500" />
                    <ShinyText text="Sign Up" speed={3} className="font-medium text-blue-500" />
                </Badge>
                <h3 className={`text-3xl md:text-5xl font-bold mb-3 md:mb-6 ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                    How to Get Started
                </h3>
                <p className={`max-w-2xl mx-auto text-base md:text-lg ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                    Our onboarding process is designed to be low-friction, allowing your clinic to start delivering personalized content within days.
                </p>
            </div>

            {/* Steps read in order: down the page on small screens, left to right on large ones */}
            <ol className="flex flex-col items-stretch gap-9 lg:flex-row lg:gap-12">
                {steps.map((step, index) => (
                    <li key={step.title} className="relative flex lg:flex-1">
                        <motion.div
                            initial={reduce ? false : { opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, delay: index * 0.08, ease: "easeOut" }}
                            className={`group relative flex w-full flex-col overflow-hidden rounded-3xl border p-6 transition-all duration-300 hover:-translate-y-1 md:p-7 ${
                                theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200 shadow-sm"
                            }`}
                            style={{ ["--c" as string]: step.color }}
                        >
                            {/* colour wash, accent edge and hover glow */}
                            <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--c) 12%, transparent), transparent 60%)" }} />
                            <div className="pointer-events-none absolute inset-x-6 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, var(--c), transparent)" }} />
                            <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-40" style={{ background: "var(--c)" }} />

                            <div className="relative mb-5">
                                <span
                                    className="select-none bg-clip-text text-6xl font-black leading-none tabular-nums text-transparent"
                                    style={{ backgroundImage: "linear-gradient(180deg, var(--c), color-mix(in srgb, var(--c) 45%, transparent))" }}
                                >
                                    0{index + 1}
                                </span>
                            </div>

                            <h4 className={`relative mb-2 text-xl font-bold ${theme === "dark" ? "text-white" : "text-slate-900"}`}>{step.title}</h4>
                            <p className={`relative text-sm leading-relaxed ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>{step.description}</p>
                        </motion.div>

                        {index < steps.length - 1 && <FlowChevrons from={step.color} to={steps[index + 1].color} reduce={reduce} />}
                    </li>
                ))}
            </ol>

            <div className="mt-8 md:mt-16 flex justify-center">
                <DemoRequestDialog triggerText="Schedule a Demo" source="provider_schedule_demo">
                    <Button size="lg" className="btn-primary text-white text-lg px-8 py-6 rounded-full shadow-xl shadow-blue-500/20">
                        <Calendar className="mr-2 w-5 h-5" />
                        Schedule a Demo
                    </Button>
                </DemoRequestDialog>

            </div>
        </div>
    );
}

export default ClinicOnboarding;
